import React from 'react';
import styled from 'styled-components';

export enum TooltipPosition {
  TopLeft = 'TopLeft',
  TopRight = 'TopRight',
  BottomLeft = 'BottomLeft',
  BottomRight = 'BottomRight',
}

const positionToCss = (position: TooltipPosition) => {
  switch (position) {
    case TooltipPosition.TopLeft:
      return 'left:0;top:0;';
    case TooltipPosition.TopRight:
      return 'right:0;top:0;';
    case TooltipPosition.BottomLeft:
      return 'left:0;bottom:0;';
    case TooltipPosition.BottomRight:
    default:
      return 'right:0;bottom:0;';
  }
};
// ############################ Styles ###########################
// tslint:disable:variable-name

const STooltipWrap = styled.section`
  display: inline-block;
  position: relative;
  vertical-align: middle;
`;

const STooltipShow = styled.section`
  display: flex;
  cursor: pointer;
`;

const STooltip = styled.section<{ position: TooltipPosition, width: number, top : number }>(({
  theme, position, width, top,
}) => `
  display: flex;
  position: absolute;
  border: 1px solid ${theme.colors.border};
  ${positionToCss(position)}
  background-color: ${theme.colors.white};
  border-radius: ${theme.borderRadiusCommon}px;
  padding: ${theme.spacing.small}px ${theme.spacing.medium}px;
  width: ${width}px;
  color: ${theme.colors.black};
  text-align: left;
  height: fit-content;
  top: ${top};
  font-size: ${theme.font.size.small};
  z-index: 1;
`);

// tslint:enable:variable-name
// ############################ End Styles ###########################

interface ITooltip {
  toggler: React.ReactNode;
  position?: TooltipPosition;
  width?: number;
  dataAt: string;
  top?:number;
  className?: string;
  onTogglerEvent?: () => void;
}

// tslint:disable-next-line:variable-name
const Tooltip: React.FC<ITooltip> = (props) => {
  const [tooltipVisible, setTooltipVisible] = React.useState(false);
  const tooltipWrapNode = React.useRef<HTMLElement>(null);

  const tooltipVisibleOff = (e: MouseEvent) => {
    if (tooltipWrapNode && tooltipWrapNode.current && tooltipWrapNode.current.contains(e.target as HTMLElement)) {
      return;
    }
    setTooltipVisible(false);
  };

  const toggleTooltipVisibleHandler = (event: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.stopPropagation();
    setTooltipVisible(!tooltipVisible);
    return false;
  };

  const toggleTooltipVisibleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    toggleTooltipVisibleHandler(event);
  };

  const toggleTooltipVisibleKey = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Enter') {
      toggleTooltipVisibleHandler(event);
    }
  };

  if (props.onTogglerEvent) {
    props.onTogglerEvent();
  }

  React.useEffect(() => {
    // add when mounted
    document.addEventListener('mousedown', tooltipVisibleOff); // return function to be called when unmounted
    // remove when unmounted
    return () => {
      document.removeEventListener('mousedown', tooltipVisibleOff);
    };
  }, []);

  return (
    <STooltipWrap ref={tooltipWrapNode} className={props.className}>
      <STooltipShow tabIndex={0} onKeyUp={toggleTooltipVisibleKey} onClick={toggleTooltipVisibleClick}>{props.toggler}</STooltipShow>
      {tooltipVisible && <STooltip data-at={`${props.dataAt}-content`} width={props.width || 100} top={props.top || 0} position={props.position || TooltipPosition.TopLeft}>{props.children}</STooltip>}
    </STooltipWrap>
  );
};

export default Tooltip;
